import axios from 'axios'

const clone = value => value && typeof value === 'object' ? JSON.parse(JSON.stringify(value)) : value
const escapeHtml = value => String(value == null ? '' : value).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
export function safeContentHtml(value) {
  // Escape first, then restore a small formatting-only vocabulary. No event/style/URL attributes.
  return escapeHtml(value).replace(/&lt;(\/?)(br|b|strong|em|i|u|p|span|ul|ol|li)(?:\s+class=&quot;([a-zA-Z0-9 _-]+)&quot;)?\s*\/?&gt;/gi,
    (_, end, tag, classes) => `<${end}${tag.toLowerCase()}${classes && !end ? ` class="${classes}"` : ''}>`)
}
export const CmsPlugin = {
  install(Vue) {
    Vue.prototype.$cms = function (scope, field, fallback) {
      const document = this.$store && this.$store.state.cms && this.$store.state.cms.document
      const component = document && document.components && document.components.find(c => c.id === scope)
      const record = component && component.fields.find(f => f.id === field)
      return clone(record ? record.value : fallback)
    }
    Vue.prototype.$cmsStyles = function (scope) {
      const document = this.$store && this.$store.state.cms && this.$store.state.cms.document
      const component = document && document.components && document.components.find(c => c.id === scope)
      const result = {}
      for (const field of component ? component.fields : []) {
        if (field.css && typeof field.value === 'string') result[`--cms-${scope}-${field.id}`] = field.value ? `url("${field.value.replace(/["\\\r\n]/g, '')}")` : 'none'
      }
      return result
    }
    Vue.prototype.$cmsHtml = safeContentHtml
    Vue.prototype.$cmsHead = function (scope, original) {
      const page = this.$store && this.$store.state.cms && this.$store.state.cms.page
      if (!page || page.componentId !== scope) return original
      const retained = (original.meta || []).filter(m => !['description', 'keywords'].includes(String(m.name).toLowerCase()))
      return { ...original, title: page.seo.title, meta: [...retained, { name: 'description', content: page.seo.description }, { name: 'keywords', content: page.seo.keywords }] }
    }
  }
}
export function createCmsModule() {
  return {
    namespaced: true,
    state: () => ({ document: null, page: null, version: null, status: 0, preview: false, initial: true }),
    mutations: {
      loaded(state, { document, page, version, preview }) { Object.assign(state, { document, page, version, preview, status: 200 }) },
      failed(state, status) { state.page = null; state.status = status },
      initialized(state) { state.initial = false }
    }
  }
}
export function installCmsRouter(router, store, context, { site, defaults }) {
  let firstNavigation = true
  router.beforeResolve(async (to, from, next) => {
    if (context && (context.isFake || context.fake)) return next()
    const state = store.state.cms
    const client = typeof window !== 'undefined'
    // Vapper restores the server's Vuex snapshot before mounting. Avoid a second initial fetch.
    const hydrate = firstNavigation && state.page && state.page.path === to.path
    firstNavigation = false
    if (client && hydrate) {
      store.commit('cms/initialized'); return next()
    }
    store.commit('cms/initialized')
    const preview = typeof to.query.__cms_preview === 'string' ? to.query.__cms_preview : ''
    const origin = client ? '/cms-api' : ((context && context.req && context.req.cmsApiOrigin) || 'http://127.0.0.1:3100')
    try {
      let document
      try {
        const response = await axios.get(`${origin}/api/content/${site}`, { params: preview ? { preview } : {}, timeout: 5000 })
        document = response.data
      } catch (error) {
        // First run has no published snapshot: render the complete migrated source defaults.
        // Never use defaults for an offline/deleted page, expired preview or network outage.
        if (!preview && error.response && error.response.data && error.response.data.code === 'NOT_PUBLISHED') document = { ...defaults, version: 'initial' }
        else throw error
      }
      const page = document.pages.find(p => p.path === to.path && (preview || p.enabled))
      if (!page) { const error = new Error('Page Not Found'); error.cmsStatus = 404; throw error }
      store.commit('cms/loaded', { document, page, version: document.version, preview: Boolean(preview) })
      next()
    } catch (cause) {
      const status = cause.cmsStatus || (cause.response && cause.response.status === 403 ? 403 : 503)
      store.commit('cms/failed', status)
      if (client) return next()
      // Vapper defaults to a SPA fallback for thrown render errors. Render the error
      // state with an explicit HTTP status so offline pages remain real 404s.
      if (context && context.res) { context.res.statusCode = status; return next() }
      const error = new Error(status === 404 ? 'Page Not Found' : status === 403 ? 'Preview Expired' : 'Content Service Unavailable')
      error.code = status; next(error)
    }
  })
}
